import React from "react";
import { useParams, Link } from "react-router-dom";
import { useSeo } from "../lib/useSeo";
import Layout from "../components/Layout";
import { Youtube, Instagram, Mail, MapPin, Send } from "lucide-react";
import { BRAND, SOCIAL, MAILTO, EMAIL_LABEL } from "../lib/brand";

const PAGES = {
  contact: {
    title: "Contact Us", hi: "संपर्क करें",
    body: [
      "Rapid24News welcomes your feedback, story ideas and queries. For all editorial, news desk, advertising, news tips and media/press enquiries, reach us at our official email below.",
      "We are based in Lucknow, Uttar Pradesh, India, with a special daily focus on Lucknow and Uttar Pradesh news.",
      "To share a news tip with our editorial team, please use our Submit News Tip page so we can verify the information.",
    ],
    contact: true,
  },
  "editorial-policy": {
    title: "Editorial Policy", hi: "संपादकीय नीति",
    body: [
      "Rapid24News is committed to accurate, fair and independent journalism. Our editorial decisions are guided by public interest, factual accuracy and responsible reporting.",
      "We verify information before publication. For crime, allegations and sensitive matters, we clearly attribute claims to their source (police, FIR, complaint, court) and never present an allegation as an established fact.",
      "Imported news from RSS feeds and authorised sources passes through de-duplication and editorial review. Sensitive stories are reviewed by an editor before publication.",
      "We attribute sources and images wherever required and do not reproduce copyrighted material without permission.",
    ],
  },
  "privacy-policy": {
    title: "Privacy Policy", hi: "गोपनीयता नीति",
    body: [
      "Rapid24News respects your privacy. We collect only the information necessary to provide and improve our services.",
      "Information submitted via forms (such as news tips) is used solely for editorial purposes and verification.",
      "We may use standard analytics to understand readership trends. We do not sell personal data to third parties.",
    ],
  },
  terms: {
    title: "Terms & Conditions", hi: "नियम एवं शर्तें",
    body: [
      "By using Rapid24News, you agree to use the website lawfully and not to misuse its content.",
      "All content is protected by applicable copyright laws. Reproduction without permission is prohibited.",
      "Rapid24News strives for accuracy but is not liable for decisions made solely on the basis of published content.",
    ],
  },
  disclaimer: {
    title: "Disclaimer", hi: "अस्वीकरण",
    body: [
      "News reports relating to crime, allegations and ongoing investigations are based on available information from police, complaints, FIRs or official statements, and do not constitute a judgment of guilt.",
      "Guilt or innocence is determined only by a court of law. Rapid24News presents such information responsibly and updates stories as facts evolve.",
      "Some content may be aggregated from third-party sources with due attribution.",
    ],
  },
  "corrections-policy": {
    title: "Corrections Policy", hi: "सुधार नीति",
    body: [
      "Rapid24News is committed to correcting errors promptly and transparently.",
      "If you believe a story contains a factual error, please contact us at rapid24news.in@gmail.com with details.",
      "Verified corrections are updated on the relevant article with a note where significant.",
    ],
  },
  "fact-check-policy": {
    title: "Fact Check Policy", hi: "तथ्य जाँच नीति",
    body: [
      "We verify claims against credible, on-record sources before publication.",
      "Where claims cannot be independently verified, we clearly attribute them and label them accordingly.",
      "We distinguish between confirmed facts, allegations, and claims under investigation.",
    ],
  },
  advertise: {
    title: "Advertise With Us", hi: "विज्ञापन",
    body: [
      "Reach an engaged audience across Lucknow, Uttar Pradesh and India with Rapid24News.",
      "We offer display advertising, sponsored content (clearly labelled) and regional campaigns.",
      "For media kits and rates, contact rapid24news.in@gmail.com.",
    ],
  },
  careers: {
    title: "Careers", hi: "करियर",
    body: [
      "Rapid24News is building a fearless digital newsroom and is always looking for talented reporters, editors and video journalists — especially those with strong Lucknow and Uttar Pradesh ground knowledge.",
      "Send your CV and work samples to rapid24news.in@gmail.com.",
    ],
  },
};

export default function StaticPage() {
  const { slug } = useParams();
  const page = PAGES[slug] || { title: "Page", hi: "", body: ["Content coming soon."] };
  useSeo({ title: page.title, description: page.body[0] });
  return (
    <Layout>
      <div className="bg-r24-black text-white">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <h1 className="font-head font-bold text-4xl md:text-5xl uc">{page.title}</h1>
          {page.hi && <p className="font-hindi text-gray-400 text-lg mt-1">{page.hi}</p>}
        </div>
      </div>
      <section className="max-w-3xl mx-auto px-4 py-10">
        <div className="article-body text-gray-800" data-testid="static-page-body">
          {page.body.map((p, i) => <p key={i}>{p}</p>)}
        </div>

        {page.contact && (
          <div className="mt-8 border-t border-gray-200 pt-8 grid sm:grid-cols-2 gap-6" data-testid="contact-details">
            <div className="space-y-4">
              <div>
                <div className="font-head font-bold uc text-xs text-gray-500 mb-1">Official Email</div>
                <a href={MAILTO} aria-label={EMAIL_LABEL} data-testid="contact-email" className="flex items-center gap-2 font-semibold text-r24-red hover:underline"><Mail size={18} /> {BRAND.email}</a>
              </div>
              <div>
                <div className="font-head font-bold uc text-xs text-gray-500 mb-1">Location</div>
                <div className="flex items-center gap-2 font-semibold"><MapPin size={18} /> {BRAND.location}</div>
              </div>
              <Link to="/submit-tip" data-testid="contact-tip-link" className="inline-flex items-center gap-2 bg-r24-red text-white font-bold uc text-sm px-5 py-3 hover:bg-r24-reddark"><Send size={16} /> Submit News Tip</Link>
            </div>
            <div>
              <div className="font-head font-bold uc text-xs text-gray-500 mb-2">Follow Rapid24News</div>
              <div className="flex flex-col gap-3">
                <a href={SOCIAL.youtube.url} target="_blank" rel="noopener noreferrer" aria-label={SOCIAL.youtube.label} data-testid="contact-youtube" className="flex items-center gap-3 border-2 border-black px-4 py-3 font-semibold hover:bg-black hover:text-white transition-colors"><Youtube size={20} /> YouTube</a>
                <a href={SOCIAL.instagram.url} target="_blank" rel="noopener noreferrer" aria-label={SOCIAL.instagram.label} data-testid="contact-instagram" className="flex items-center gap-3 border-2 border-black px-4 py-3 font-semibold hover:bg-black hover:text-white transition-colors"><Instagram size={20} /> Instagram</a>
              </div>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}
